/**
 * @file: ReservationHeaderCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The reservation-detail info panel. It anchors the booking in two protagonists:
 * the license-plate token and the expected-arrival time with an at-a-glance
 * urgency read. The vehicle/customer info rows and the create/update timestamps
 * sit quietly beneath; arrival is the lone editable field. Pure presentation.
 */

import { Typography } from '@euler/app/components/typography';
import {
  Field,
  LD,
  cardShadowStyle,
} from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { OrderTagList } from '@euler/app/flows/order/components/OrderTagList';
import { InlineMileageRow } from '@euler/app/flows/reservations/pages/detail/components/InlineMileageRow';
import { reservationStatusTone } from '@euler/app/flows/reservations/pages/list/components/reservation-card';
import { StatusPill } from '@euler/components/StatusPill';
import { getCustomerTypeInfo } from '@euler/functions/getCustomerTypes';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  Reservation,
  ReservationStatusLabel,
  isTerminalReservationStatus,
} from '@euler/model/reservation/Reservation';
import { dateFromValue } from '@euler/utils/datetime';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import format from 'date-fns/format';
import { CalendarClock, Pencil, Phone } from 'lucide-react-native';
import { memo } from 'react';
import { Linking, TouchableOpacity, View } from 'react-native';

const formatDateTime = (value: string | Date | undefined | null): string => {
  if (!value) return '—';
  try {
    return format(dateFromValue(value), 'yyyy-MM-dd HH:mm');
  } catch {
    return '—';
  }
};

// Calendar days until the expected arrival, or null when unreadable.
const daysUntil = (value: string | Date | undefined | null): number | null => {
  if (!value) return null;
  try {
    return differenceInCalendarDays(dateFromValue(value), new Date());
  } catch {
    return null;
  }
};

export const ReservationHeaderCard = memo(
  ({
    reservation,
    onEditArrival,
    onEditAgent,
    onAddTag,
    onRemoveTag,
    onEditCustomerType,
    onSaveMileage,
    onOpenOrder,
  }: {
    reservation: Reservation;
    onEditArrival?: () => void;
    // When an editor callback is provided the matching row becomes editable
    // (tappable / inline). Omitted (terminal reservation) → read-only.
    onEditAgent?: () => void;
    onAddTag?: (tag: string) => void;
    onRemoveTag?: (tag: string) => void;
    onEditCustomerType?: () => void;
    onSaveMileage?: (value: string) => void;
    // Open the linked order's service record (when the booking is converted).
    onOpenOrder?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const terminal = isTerminalReservationStatus(reservation.status);

    const brand = reservation.vehicleBrandName?.trim();
    const rawModel = reservation.vehicleModel?.trim();
    const model =
      brand && rawModel?.startsWith(brand)
        ? rawModel.slice(brand.length).trim()
        : rawModel;
    const vehicleLabel = [brand, model].filter(Boolean).join(' · ');

    // Urgency is only meaningful while the visit is still expected.
    const days = terminal ? null : daysUntil(reservation.expectedArrivalAt);
    let urgency: { text: string; fg: string; bg: string } | null = null;
    if (days != null) {
      const tone =
        days < 0
          ? { fg: LD.danger, bg: LD.dangerSoft }
          : days <= 1
          ? { fg: LD.amber, bg: LD.amberSoft }
          : { fg: LD.steel, bg: LD.hairline };
      const text =
        days < 0
          ? `已过预计 ${-days} 天`
          : days === 0
          ? '今天到店'
          : days === 1
          ? '明天到店'
          : `还有 ${days} 天`;
      urgency = { text, ...tone };
    }

    const customer = [reservation.customerName, reservation.customerPhone]
      .filter(Boolean)
      .join(' · ');
    const tagList = reservation.tags
      ? reservation.tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean)
      : [];
    const tagsLabel = tagList.join(' · ');
    const customerTypeLabel = reservation.customerType
      ? getCustomerTypeInfo(reservation.customerType).name
      : '';
    const mileageLabel =
      reservation.vehicleMileage != null
        ? `${reservation.vehicleMileage} 公里`
        : '';

    return (
      <View
        style={{
          backgroundColor: LD.surface,
          borderRadius: rpx(14),
          paddingTop: rpx(16),
          paddingHorizontal: rpx(16),
          paddingBottom: rpx(6),
          marginBottom: rpx(10),
          ...cardShadowStyle(rpx),
        }}
      >
        {/* plate-initial chip anchors the identity block; plate + vehicle stack
            to its right, status top-right */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View
            style={{
              width: rpx(52),
              height: rpx(52),
              borderRadius: rpx(15),
              backgroundColor: LD.brandSoft,
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: rpx(12),
            }}
          >
            <Typography.Text size={rpx(20)} color={LD.brand} bold>
              {reservation.licensePlateNo?.[0] ?? '车'}
            </Typography.Text>
          </View>
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography.Text
                size={rpx(22)}
                color={LD.ink}
                bold
                letterSpacing={rpx(1)}
                numberOfLines={1}
                style={{ flex: 1, paddingRight: rpx(8) }}
              >
                {reservation.licensePlateNo || '未知车牌'}
              </Typography.Text>
              <StatusPill
                label={ReservationStatusLabel[reservation.status]}
                tone={reservationStatusTone(reservation.status)}
              />
            </View>
            <Typography.Text
              size={rpx(13)}
              color={LD.steel}
              medium
              numberOfLines={1}
              mt={rpx(4)}
            >
              {vehicleLabel || reservation.vehicleName || '车辆信息待补充'}
            </Typography.Text>
          </View>
        </View>

        <View
          style={{
            height: 1,
            backgroundColor: LD.hairline,
            marginTop: rpx(14),
            marginBottom: rpx(12),
          }}
        />

        {/* protagonist arrival, editable, with the dial button trailing it */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity
            activeOpacity={onEditArrival ? 0.6 : 1}
            disabled={!onEditArrival}
            onPress={onEditArrival}
            style={{ flex: 1 }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <CalendarClock size={rpx(12)} color={LD.mist} />
              <Typography.Text
                size={rpx(11)}
                color={LD.mist}
                letterSpacing={rpx(0.5)}
                style={{ marginLeft: rpx(4) }}
              >
                预计到店
              </Typography.Text>
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: rpx(4),
              }}
            >
              <Typography.Text size={rpx(19)} color={LD.ink} medium>
                {formatDateTime(reservation.expectedArrivalAt)}
              </Typography.Text>
              {onEditArrival ? (
                <Pencil
                  size={rpx(14)}
                  color={LD.brand}
                  style={{ marginLeft: rpx(8) }}
                />
              ) : null}
              {urgency ? (
                <View
                  style={{
                    marginLeft: rpx(10),
                    backgroundColor: urgency.bg,
                    borderRadius: rpx(11),
                    paddingHorizontal: rpx(9),
                    paddingVertical: rpx(3),
                  }}
                >
                  <Typography.Text size={rpx(11)} color={urgency.fg} medium>
                    {urgency.text}
                  </Typography.Text>
                </View>
              ) : null}
            </View>
          </TouchableOpacity>
          {reservation.customerPhone ? (
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() =>
                Linking.openURL(`tel:${reservation.customerPhone}`)
              }
              style={{
                marginLeft: rpx(10),
                width: rpx(40),
                height: rpx(40),
                borderRadius: rpx(20),
                backgroundColor: LD.brand,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Phone size={rpx(19)} color={LD.surface} />
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={{ marginTop: rpx(6) }}>
          <Field
            label="客户信息"
            value={customer}
            divider={false}
            emptyText="(暂无)"
          />
          <Field
            label="车辆"
            value={reservation.vehicleName || vehicleLabel}
            emptyText="(暂无)"
          />
          <Field
            label="服务顾问"
            value={reservation.serviceAgentName}
            onPress={onEditAgent}
            affordance="chevron"
          />
          {onAddTag ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: rpx(44),
                borderTopWidth: 1,
                borderTopColor: LD.hairline,
                paddingVertical: rpx(8),
              }}
            >
              <Typography.Text
                size={rpx(13)}
                color={LD.steel}
                style={{ marginRight: rpx(12) }}
              >
                业务类型
              </Typography.Text>
              <OrderTagList
                tags={tagList}
                onAddTag={onAddTag}
                onTagRemoved={onRemoveTag}
                placeholder="选择业务类型"
                style={{ flex: 1, justifyContent: 'flex-end' }}
              />
            </View>
          ) : (
            <Field label="业务类型" value={tagsLabel} />
          )}
          {onEditCustomerType ? (
            <Field
              label="客户类型"
              value={customerTypeLabel}
              onPress={onEditCustomerType}
              affordance="chevron"
            />
          ) : customerTypeLabel ? (
            <Field label="客户类型" value={customerTypeLabel} />
          ) : null}
          {onSaveMileage ? (
            <InlineMileageRow
              mileage={reservation.vehicleMileage}
              editable
              onSave={onSaveMileage}
            />
          ) : mileageLabel ? (
            <Field label="预估里程" value={mileageLabel} />
          ) : null}
          {reservation.orderNo ? (
            <TouchableOpacity
              activeOpacity={onOpenOrder ? 0.6 : 1}
              disabled={!onOpenOrder}
              onPress={onOpenOrder}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: rpx(44),
                borderTopWidth: 1,
                borderTopColor: LD.hairline,
              }}
            >
              <Typography.Text
                size={rpx(13)}
                color={LD.steel}
                style={{ marginRight: rpx(12) }}
              >
                关联订单
              </Typography.Text>
              <Typography.Text
                size={rpx(14)}
                color={LD.brand}
                medium
                style={{ flex: 1, textAlign: 'right' }}
              >
                查看订单 →
              </Typography.Text>
            </TouchableOpacity>
          ) : null}
          {/* timestamp rows */}
          <Field
            label="创建时间"
            value={`${formatDateTime(reservation.createdAt)}${
              reservation.createdByName ? ` · ${reservation.createdByName}` : ''
            }`}
          />
          {reservation.updatedAt ? (
            <Field
              label="更新时间"
              value={`${formatDateTime(reservation.updatedAt)}${
                reservation.updatedByName
                  ? ` · ${reservation.updatedByName}`
                  : ''
              }`}
            />
          ) : null}
        </View>
      </View>
    );
  },
);
